import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { User, Star, Package, Calendar, Award } from 'lucide-react';
import { DbProduct } from '../../utils/api';
import ProductCard from './ProductCard';
import SellerRating from './SellerRating';

const API_URL = window.location.protocol + '//' + window.location.host + '/api';

interface SellerInfo {
  id: number;
  name: string;
  club: string;
  created_at: string;
}

const SellerProfile = () => {
  const { id } = useParams<{ id: string }>();
  const [seller, setSeller] = useState<SellerInfo | null>(null);
  const [products, setProducts] = useState<DbProduct[]>([]);
  const [ratingAvg, setRatingAvg] = useState<number | null>(null);
  const [ratingCount, setRatingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    fetch(`${API_URL}/seller.php?id=${id}`, { credentials: 'include' })
      .then(r => r.json())
      .then(res => {
        if (res.success) {
          setSeller(res.seller);
          setProducts(res.products);
          setRatingAvg(res.rating.average);
          setRatingCount(res.rating.count);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <p className="text-gray-500">Verkäufer nicht gefunden.</p>
        <Link to="/products" className="text-orange-500 mt-4 inline-block hover:underline">Zurück zu den Produkten</Link>
      </div>
    );
  }

  const memberSince = new Date(seller.created_at).toLocaleDateString('de-DE', { year: 'numeric', month: 'long' });

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Profil-Header */}
      <div className="bg-white rounded-2xl shadow-md p-6 mb-8 flex flex-col sm:flex-row items-center sm:items-start gap-6">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center flex-shrink-0">
          <User className="w-10 h-10 text-white" />
        </div>
        <div className="text-center sm:text-left">
          <h1 className="text-2xl font-bold text-gray-800">{seller.name}</h1>
          <p className="text-gray-500 mt-1">{seller.club || 'Vereinslos'}</p>
          <div className="flex flex-wrap justify-center sm:justify-start gap-4 mt-3 text-sm text-gray-500">
            {ratingAvg !== null && (
              <span className="flex items-center gap-1">
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                {ratingAvg.toFixed(1)} ({ratingCount} {ratingCount === 1 ? 'Bewertung' : 'Bewertungen'})
              </span>
            )}
            {ratingAvg !== null && ratingAvg >= 4.5 && ratingCount >= 3 && (
              <span className="flex items-center gap-1 bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                <Award className="w-3 h-3" /> Top Seller
              </span>
            )}
            <span className="flex items-center gap-1">
              <Package className="w-4 h-4" />
              {products.length} {products.length === 1 ? 'Angebot' : 'Angebote'}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Dabei seit {memberSince}
            </span>
          </div>
        </div>
      </div>

      {/* Produkte */}
      <h2 className="text-xl font-bold text-gray-800 mb-4">Angebote von {seller.name}</h2>
      {products.length === 0 ? (
        <p className="text-gray-500 text-center py-8">Keine aktiven Angebote.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
          {products.map(p => <ProductCard key={p.id} {...p} />)}
        </div>
      )}

      {/* Bewertungen */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <SellerRating sellerId={seller.id} sellerName={seller.name} />
      </div>
    </div>
  );
};

export default SellerProfile;
